interface Props {
  /**
   * `'full'` renders the labeled "★ Floor Manager" pill used in dense surfaces
   * with room for the label (profile modal header, on-duty FM tile).
   *
   * `'compact'` renders the star glyph alone for narrow surfaces where the
   * label would push the layout (presence-roster member row, instance-
   * participants chip header). Identical semantics — same `title` tooltip,
   * same `aria-label` — so screen readers and hover-tooltips behave the same
   * regardless of which variant the surface chose.
   */
  readonly variant: 'full' | 'compact';
}

/**
 * Single source of truth for "this user is the on-duty Floor Manager" badging
 * across every Activity surface (#4107). Replaces the inline star in
 * `<PresenceMemberRow>` and the inline labeled badge in the profile modal
 * header — both call-sites now compose this component with the appropriate
 * variant, so any future treatment change (color, glyph, hover) lives in one
 * file.
 *
 * Class names follow the existing `presence-row__fm-star` / `profile-modal__badge`
 * patterns to preserve the per-surface styling already in `presence.css` /
 * `profile-modal.css`. The `floor-manager-badge` base class layers shared
 * concerns on top (e.g. ensuring the star glyph never inherits text color
 * from a hover state).
 */
export function FloorManagerBadge({ variant }: Props) {
  if (variant === 'compact') {
    return (
      <span
        className="floor-manager-badge floor-manager-badge--compact"
        title="Floor Manager"
        aria-label="Floor Manager"
      >
        ★
      </span>
    );
  }
  return (
    <span
      className="floor-manager-badge floor-manager-badge--full"
      title="Floor Manager"
    >
      <i className="fa-solid fa-star" aria-hidden="true" />
      Floor Manager
    </span>
  );
}
